import { inject } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest
} from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { ErrorLibrary } from 'siq_errors';
import { LocalStorageService, SessionStorageService } from 'ngx-webstorage';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { GeenVerbindingService } from '../services/geen-verbinding.service';

export const NO_ERROR_TOKEN = new HttpContextToken<string[] | boolean>(() => false);

export const errorInterceptorProvider = (request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
  const errorLibrary = inject(ErrorLibrary);
  const localStorageService = inject(LocalStorageService);
  const toastrService = inject(ToastrService);
  // Hier ophalen en niet pas bij de fout: inject() werkt alleen zolang de
  // interceptor zelf loopt. Tot 21-10-2026 gebeurde het in
  // logoutUserAndRedirect, dus pas in catchError, als het antwoord al binnen
  // was. Dan gooit inject() NG0203, en een verlopen sessie (na 24 uur) liep
  // nooit naar het inlogscherm: elke pagina bleef leeg ("No cameras found",
  // geen video's), ook na herladen, tot iemand zelf uit- en inlogde.
  const authService = inject(AuthService);
  const sessionStorageService = inject(SessionStorageService);
  const router = inject(Router);
  const geenVerbinding = inject(GeenVerbindingService);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // Helemaal geen antwoord van PlayStats: geen internet (of het platform
      // is weg). Dan komt Emergency control in beeld en gaan tags naar de pc.
      if (error.status === 0 && request.url.startsWith(environment.apiUrl)) {
        geenVerbinding.meld();
      }
      if (checkErrorContext(request, error)) {
        return throwError(() => error);
      }

      // If token is not valid, redirect to login page
      if (error.status === 401) {
        logoutUserAndRedirect(authService, sessionStorageService, router);
        return EMPTY;
      }

      // Een 403 zonder body (bijv. de kijklink zonder het recht "live
      // meekijken") heeft geen `error.error`; zonder `?.` gooide deze regel
      // een TypeError, waardoor de aanroeper nooit de echte 403 zag en de
      // Live-pagina "even geen beeld" meldde in plaats van het ontbrekende
      // recht (13-09-2026).
      const message = errorLibrary.getMessage(error.error?.Key, localStorageService.retrieve('language') ?? environment.defaultLanguage);

      if (message) {
        toastrService.error(message);
      }

      if (error.status === 500) {
        toastrService.error('Server error. Please try again later');
      }

      return throwError(() => error);
    })
  )
}

const checkErrorContext = (request: HttpRequest<unknown>, error: HttpErrorResponse): boolean => {
  const noErrorContext = request.context.get(NO_ERROR_TOKEN);

  return typeof noErrorContext === 'boolean' && noErrorContext === true
    || typeof noErrorContext === 'object' && noErrorContext.includes(error.error?.Key);
}

// Een pagina vraagt vaak meerdere dingen tegelijk op; na een verlopen sessie
// komen die allemaal met 401 terug. Eén keer uitloggen en doorsturen is
// genoeg, en anders zou de tweede het inlogscherm als terugkeeradres bewaren.
// Ook app.component (bewaakSessie) stuurt hierheen, met dezelfde bewaking.
// Na het herladen begint dit weer op false.
let bezigMetUitloggen = false;

/** Alleen voor de specs: de stand van hierboven terugzetten. */
export const vergeetUitloggenVoorTest = (): void => {
  bezigMetUitloggen = false;
};

export const logoutUserAndRedirect = (authService: AuthService, sessionStorageService: SessionStorageService, router: Router): void => {
  if (bezigMetUitloggen) {
    return;
  }
  bezigMetUitloggen = true;

  authService.logout();
  sessionStorageService.store('redirectUrl', router.url);
  router.navigateByUrl('/auth/login').then(() => {
    window.location.reload();
  });
}